import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { BulkMedicationsItem } from 'src/app/models/bulk-medications.model';
import {
  MedicationCeilingContext,
  isAtOrAboveCeiling,
  isDoseUnknown,
  resolveLoopDiureticDose,
} from 'src/app/patient/careplan/fhir-medications/loop-diuretic-dose.util';
import {
  Medication,
  MedicationDoseMeasure,
  MedicationDoseType,
  MedicationHours,
  MedicationReminder,
  MedicationReminderFrequency,
  MedicationStatus,
  MedicationStatusReason,
  TimePeriod,
  WeekDay,
} from 'src/app/models/fhir-models';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { PatientDetailService } from 'src/app/services/patient-detail.service';
import { PatientService } from 'src/app/services/patient.service';
import { SnackService } from 'src/app/services/snack.service';
import { ThemeService } from 'src/app/services/theme.service';
import { UtilsService } from 'src/app/services/utils.service';
import { AreYouSureModalComponent } from 'src/app/shared/are-you-sure-modal/are-you-sure-modal.component';
import { MedicationFrequencyOption, NihAutoComplete } from '../../fhir-medications.model';
import { FhirMedicationsService } from '../../fhir-medications.service';

export const otherFrequencyValidator = (group: AbstractControl): ValidationErrors | null => {
  const frequency = group.get('frequency')?.value;
  const otherFrequency = group.get('otherFrequency')?.value;
  if (frequency === 'Other' && !String(otherFrequency ?? '').trim()) {
    return { otherFrequencyRequired: true };
  }
  return null;
};

@Component({
  selector: 'app-fhir-medication-detail',
  templateUrl: './fhir-medication-detail.component.html',
  styleUrls: ['./fhir-medication-detail.component.scss'],
})
export class FhirMedicationDetailComponent implements OnInit, OnDestroy {
  readonly maxBulkMedications = 20;
  medicationsArray: UntypedFormArray;
  expandedIndex = 0;
  filteredOptions: Observable<string[]>[] = [];
  options: string[] = [];
  hours = Object.values(MedicationHours);
  doseTypes = Object.values(MedicationDoseType);
  doses = Object.values(MedicationDoseMeasure);
  frequencyOptions: MedicationFrequencyOption[] = [];
  statusOptions = Object.values(MedicationStatus).map((value) => ({
    value,
    label: this.formatEnumLabel(value),
  }));
  statusReasonOptions = Object.values(MedicationStatusReason);
  searching = false;
  loading = false;
  availablePriorMedications: Medication[] = [];
  readonly reminderFrequencyOptions = Object.values(MedicationReminderFrequency).map((value) => ({
    value,
    name: this.formatReminderFrequencyLabel(value),
  }));
  readonly days = Object.values(WeekDay).map((value) => ({
    key: value,
    label: this.getWeekDayLabel(value),
  }));
  readonly periods = Object.values(TimePeriod);
  hourOptions: string[] = [];
  reminderId: string;
  isLightMode = false;
  isEditMode = false;
  destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<FhirMedicationDetailComponent>,
    private fb: UntypedFormBuilder,
    private fhirService: FhirMedicationsService,
    private patientService: PatientService,
    private patientDetail: PatientDetailService,
    @Inject(MAT_DIALOG_DATA) public data: Medication & { ceilingContext?: MedicationCeilingContext },
    private snackBarService: SnackService,
    private themeService: ThemeService,
    private authService: NewAuthService,
    private utilsService: UtilsService,
    private dialog: MatDialog
  ) {
    this.hourOptions = this.utilsService.generateHours();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboard(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      this.addAnotherMedication();
      return;
    }
    if (event.key === 'Escape') {
      this.collapseActiveIfValid();
    }
  }

  async ngOnInit(): Promise<void> {
    this.frequencyOptions = await this.fhirService.getOptionsFrequencyMedications();
    this.isEditMode = !!this.data?.id;
    this.loadAvailableMedications();
    this.initializeForm();
    this.getDisplayNames();
    this.listenChangeTheme();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get medicationCount(): number {
    return this.medicationsArray?.length ?? 0;
  }

  get title(): string {
    if (this.isEditMode) {
      return 'Edit Medication';
    }
    return this.medicationCount > 1 ? 'Add medications' : 'Add Medication';
  }

  get saveLabel(): string {
    return this.medicationCount > 1 ? `Save all (${this.medicationCount})` : 'Save';
  }

  get canAddAnother(): boolean {
    return !this.isEditMode;
  }

  get atMedicationLimit(): boolean {
    return this.medicationCount >= this.maxBulkMedications;
  }

  loadAvailableMedications(): void {
    this.patientDetail.medications$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (medications) => {
        this.availablePriorMedications = medications.filter((med) => med.id !== this.data?.id);
      },
      error: (error) => {
        console.error('Error loading available medications:', error);
        this.snackBarService.genericSnackBar('Error loading available medications', ['error-snackbar']);
      },
    });
  }

  isValidFrequency(frequency: string | undefined): boolean {
    return this.frequencyOptions.some((option) => option.value === frequency);
  }

  isExpanded(index: number): boolean {
    return this.expandedIndex === index;
  }

  expandCard(index: number): void {
    this.expandedIndex = index;
  }

  removeStagedCard(index: number): void {
    if (this.medicationsArray.length <= 1) {
      return;
    }
    this.medicationsArray.removeAt(index);
    this.filteredOptions.splice(index, 1);
    if (this.expandedIndex === index) {
      this.expandedIndex = Math.min(index, this.medicationsArray.length - 1);
    } else if (this.expandedIndex > index) {
      this.expandedIndex -= 1;
    }
  }

  addAnotherMedication(): void {
    if (this.isEditMode || this.atMedicationLimit) {
      return;
    }
    const active = this.medicationsArray.at(this.expandedIndex) as UntypedFormGroup;
    if (!active || active.invalid) {
      active?.markAllAsTouched();
      return;
    }

    const seed = this.buildSmartDefaults(active);
    const newGroup = this.buildMedicationGroup(seed);
    this.medicationsArray.push(newGroup);
    this.attachNameFilter(newGroup);
    this.expandedIndex = this.medicationsArray.length - 1;
  }

  collapseActiveIfValid(): void {
    const active = this.medicationsArray.at(this.expandedIndex) as UntypedFormGroup;
    if (active && active.valid) {
      this.expandedIndex = -1;
    }
  }

  getSummary(index: number): string {
    const group = this.medicationsArray.at(index) as UntypedFormGroup;
    if (!group) {
      return '';
    }
    const amount = group.get('dosageAmount')?.value ?? '';
    const measure = group.get('dosageMeasure')?.value ?? '';
    const frequency = this.resolveFrequency(group);
    const hours = this.serializeHours(group).join(', ');
    const dosePart = [amount, measure].filter(Boolean).join(' ');
    const freqPart = [frequency, hours].filter(Boolean).join(' ');
    return [dosePart, freqPart].filter(Boolean).join(' · ');
  }

  getSummaryName(index: number): string {
    const group = this.medicationsArray.at(index) as UntypedFormGroup;
    return group?.get('name')?.value ?? '';
  }

  isDuplicateName(index: number): boolean {
    const name = this.medicationsArray.at(index)?.get('name')?.value;
    if (!name) {
      return false;
    }
    const lower = String(name).toLowerCase();
    if (this.availablePriorMedications.some((med) => med.name?.toLowerCase() === lower)) {
      return true;
    }
    for (let i = 0; i < index; i++) {
      const otherName = (this.medicationsArray.at(i) as UntypedFormGroup).get('name')?.value;
      if (otherName && String(otherName).toLowerCase() === lower) {
        return true;
      }
    }
    return false;
  }

  shouldShowStatusReason(index: number): boolean {
    const status = this.medicationsArray.at(index)?.get('status')?.value;
    return status === MedicationStatus.ON_HOLD || status === MedicationStatus.STOPPED;
  }

  reminderTimesAt(index: number): UntypedFormArray {
    return this.medicationsArray.at(index).get('reminderTimes') as UntypedFormArray;
  }

  reminderDaysAt(index: number): UntypedFormGroup {
    return this.medicationsArray.at(index).get('reminderDays') as UntypedFormGroup;
  }

  addTime(index: number): void {
    this.reminderTimesAt(index).push(this.createTime(''));
  }

  removeTime(index: number, idx: number): void {
    this.reminderTimesAt(index).removeAt(idx);
  }

  selectedSet(index: number): Set<string> {
    return new Set<string>((this.reminderTimesAt(index).value as string[]).filter(Boolean));
  }

  isDisabledOption(label: string, index: number, rowIndex: number): boolean {
    const current = this.reminderTimesAt(index).at(rowIndex).value as string;
    return label !== current && this.selectedSet(index).has(label);
  }

  setSelectedDays(index: number, day: string): void {
    const ctrl = this.reminderDaysAt(index).get(day);
    if (ctrl) {
      ctrl.setValue(!ctrl.value);
    }
  }

  isSelected(index: number, key: string): boolean {
    const ctrl = this.reminderDaysAt(index).get(key);
    return !!ctrl && !!ctrl.value;
  }

  selectedDayClass(index: number, key: string): string {
    if (!this.isSelected(index, key)) {
      return '';
    }
    return this.isLightMode ? 'light-selected-day-btn' : 'dark-selected-day-btn';
  }

  async onSave(): Promise<void> {
    if (this.medicationsArray.invalid) {
      this.medicationsArray.markAllAsTouched();
      return;
    }
    if (this.isEditMode) {
      await this.saveSingleEdit();
    } else {
      await this.saveAllNew();
    }
  }

  onCancel(): void {
    if (this.hasStagedWork()) {
      const confirmRef = this.dialog.open(AreYouSureModalComponent, {
        width: '500px',
        data: {
          title: 'Discard medications?',
          body: `Discard ${this.medicationCount} ${this.medicationCount === 1 ? 'medication' : 'medications'}?`,
          confirmButton: 'Discard',
          cancelButton: 'Keep editing',
        },
      });
      confirmRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((confirmed) => {
          if (confirmed) {
            this.dialogRef.close();
          }
        });
      return;
    }
    this.dialogRef.close();
  }

  private hasStagedWork(): boolean {
    if (this.isEditMode) {
      return false;
    }
    if (this.medicationsArray.length > 1) {
      return true;
    }
    const single = this.medicationsArray.at(0) as UntypedFormGroup;
    return !!single && (single.dirty || single.touched);
  }

  private async saveAllNew(): Promise<void> {
    try {
      this.loading = true;
      const items = this.medicationsArray.controls.map((ctrl) => this.toBulkItem(ctrl as UntypedFormGroup));
      const patientId = this.patientService.currentPatientServiceID;

      const saved = await this.fhirService.bulkAddMedicationsToPatient(patientId, items).toPromise();

      const reminderResult = await this.saveRemindersForSaved(patientId, saved);

      const count = saved?.length ?? 0;
      const baseMessage = count > 1 ? `${count} medications saved` : 'Medication saved';
      const message =
        reminderResult.failed > 0
          ? `${baseMessage} (${reminderResult.failed} of ${reminderResult.attempted} reminders failed)`
          : baseMessage;
      const cssClass = reminderResult.failed > 0 ? 'warning-snackbar' : 'success-snackbar';
      this.snackBarService.genericSnackBar(message, [cssClass]);
      this.dialogRef.close();
    } catch (error) {
      this.snackBarService.genericSnackBar('Error saving medications', ['error-snackbar']);
    } finally {
      this.loading = false;
    }
  }

  private async saveSingleEdit(): Promise<void> {
    try {
      this.loading = true;
      const group = this.medicationsArray.at(0) as UntypedFormGroup;
      const item = this.toBulkItem(group);
      const patientId = this.patientService.currentPatientServiceID;
      const medicationId = group.get('id')?.value;

      const medicationSaved = await this.fhirService
        .updateMedicationForPatient(patientId, medicationId, item)
        .toPromise();

      if (medicationSaved?.id) {
        this.saveEditReminder(medicationSaved.id, group);
      }

      this.snackBarService.genericSnackBar('Medication saved', ['success-snackbar']);
      this.dialogRef.close();
    } catch (error) {
      this.snackBarService.genericSnackBar('Error saving medication', ['error-snackbar']);
    } finally {
      this.loading = false;
    }
  }

  private async saveRemindersForSaved(
    patientId: string,
    saved: Medication[]
  ): Promise<{ attempted: number; failed: number }> {
    if (!saved?.length) {
      return { attempted: 0, failed: 0 };
    }
    const calls: Observable<MedicationReminder | null>[] = [];
    saved.forEach((savedMed, idx) => {
      const group = this.medicationsArray.at(idx) as UntypedFormGroup;
      if (!group?.get('reminder')?.value) {
        return;
      }
      const reminder: MedicationReminder = {
        medicationId: savedMed.id,
        isActive: true,
        frequency: group.get('reminderFrequency')?.value || '',
        days: group.get('reminderDays')?.value,
        times: group.get('reminderTimes')?.value || [],
      };
      calls.push(
        this.fhirService
          .saveMedicationReminder(patientId, reminder)
          .pipe(catchError(() => of<MedicationReminder | null>(null)))
      );
    });

    if (!calls.length) {
      return { attempted: 0, failed: 0 };
    }

    const results = await forkJoin(calls).toPromise();
    const failed = results.filter((r) => r === null).length;
    return { attempted: calls.length, failed };
  }

  private saveEditReminder(medicationId: string, group: UntypedFormGroup): void {
    try {
      const reminder: MedicationReminder = {
        ...(this.reminderId && { id: this.reminderId }),
        medicationId,
        isActive: group.get('reminder')?.value,
        frequency: group.get('reminderFrequency')?.value || '',
        days: group.get('reminderDays')?.value,
        times: group.get('reminderTimes')?.value || [],
      };

      const obs = this.reminderId
        ? this.fhirService.updateMedicationReminder(this.patientService.currentPatientServiceID, reminder)
        : this.fhirService.saveMedicationReminder(this.patientService.currentPatientServiceID, reminder);

      obs.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => this.snackBarService.genericSnackBar('Medication reminder saved', ['success-snackbar']),
        error: (error) => {
          console.error('Error saving medication reminder:', error);
          this.snackBarService.genericSnackBar('Error saving medication reminder', ['error-snackbar']);
        },
      });
    } catch (error) {
      console.error(error);
      this.snackBarService.genericSnackBar('Error saving medication reminder', ['error-snackbar']);
    }
  }

  /**
   * BRD-CLIN-2026-006 §4.2's ceiling for the row being edited, or '' when it does not apply.
   *
   * Computed LIVE from the form rather than on save, which is the more useful moment: the MA sees
   * "doubling this reaches the ceiling" while they are still typing the dose, not after committing
   * it. `ceilingContext` is absent for a clinic outside the SOC or a non-CHF patient, and then this
   * returns '' and nothing renders.
   *
   * Reads the same util the list row uses, so the modal and the row behind it cannot disagree.
   */
  /**
   * The ceiling note for one medication row: its text and whether it is the WARNING state.
   *
   * ONE METHOD, ONE CALL. This was two — `ceilingHintFor` and `isCeilingWarning` — each re-reading the
   * same four controls and re-running `resolveLoopDiureticDose`. The template called them from an
   * `*ngIf`, an interpolation and two class bindings, so every change-detection cycle rebuilt the
   * drug-matching Set and rescanned both config arrays about five times per row. Raised in review on
   * #1261, along with the more durable half: the form-reading block was duplicated verbatim, so
   * normalising a unit in one and not the other would have desynced the icon from the text it sits
   * next to, silently.
   *
   * Read it once with `*ngIf="ceilingNoteFor(group) as note"`, then use `note.text` and `note.warn`.
   * Returns null when there is nothing to show, which is what makes the `as` binding the guard too.
   */
  ceilingNoteFor(group: UntypedFormGroup): { text: string; warn: boolean } | null {
    const context = this.data?.ceilingContext;
    if (!context) {
      return null;
    }
    const dose = resolveLoopDiureticDose(
      {
        name: group.get('name')?.value,
        dosageAmount: group.get('dosageAmount')?.value,
        dosageMeasure: group.get('dosageMeasure')?.value,
        frequency: group.get('frequency')?.value,
      } as Medication,
      context.drugs,
      context.frequencies,
    );
    if (!dose) {
      return null;
    }
    // A MISCONFIGURED CEILING IS NOT THE MA'S TO FIX, raised in review on #1261. The reason type
    // promises each value "maps to something a human can fix"; this one is fixed by an engineer, and
    // asking for a dose and unit that are already filled in sends the MA hunting for nothing.
    if (dose.reason === 'no_ceiling') {
      return { text: 'Ceiling not configured for this medication — the daily dose cannot be checked against one.', warn: false };
    }
    if (isDoseUnknown(dose)) {
      // An INCOMPLETE record, not a safe one — worded as a prompt rather than an alarm, and styled
      // apart, because dressing this as the warning teaches people to dismiss the real one.
      return { text: 'Daily dose cannot be calculated yet — complete the dose, unit and frequency.', warn: false };
    }
    return isAtOrAboveCeiling(dose)
      ? {
          text: `${dose.mgPerDay} mg/day is at or above the ${dose.ceilingMgDay} mg/day ceiling. No further titration without provider input.`,
          warn: true,
        }
      : null;
  }

  private toMedication(group: UntypedFormGroup): Medication {
    return {
      dateWritten: new Date(),
      dosage: `${group.get('dosageAmount')?.value} ${group.get('dosageMeasure')?.value} ${
        group.get('drugForm')?.value ?? ''
      }`.trim(),
      id: group.get('id')?.value,
      medref: 'N/A',
      name: group.get('name')?.value,
      prescriber: this.authService.user
        ? `${this.authService.user.lastName}, ${this.authService.user.firstName}`
        : 'TBD',
      dosageAmount: group.get('dosageAmount')?.value,
      dosageMeasure: group.get('dosageMeasure')?.value,
      form: group.get('drugForm')?.value,
      frequency: this.resolveFrequency(group),
      hours: this.serializeHours(group),
      status: group.get('status')?.value,
      statusReason: this.shouldShowStatusReasonForGroup(group) ? group.get('statusReason')?.value : '',
      priorPrescription: group.get('priorPrescription')?.value,
    };
  }

  private toBulkItem(group: UntypedFormGroup): BulkMedicationsItem {
    const med = this.toMedication(group);
    return {
      name: med.name,
      dosage: med.dosage,
      dosageAmount: String(med.dosageAmount ?? ''),
      dosageMeasure: med.dosageMeasure,
      form: med.form,
      frequency: med.frequency,
      hours: med.hours,
      status: med.status,
      statusReason: med.statusReason,
      priorPrescription: med.priorPrescription,
      prescriber: med.prescriber,
      medref: med.medref,
    };
  }

  private serializeHours(group: UntypedFormGroup): string[] {
    const sub = group.get('selectedHours');
    if (!(sub instanceof UntypedFormGroup)) {
      return [];
    }
    return Object.values(MedicationHours).filter((h: string) => !!sub.get(h)?.value);
  }

  private resolveFrequency(group: UntypedFormGroup): string {
    const value = group.get('frequency')?.value;
    if (value === 'Other') {
      return group.get('otherFrequency')?.value ?? '';
    }
    return value ?? '';
  }

  private shouldShowStatusReasonForGroup(group: UntypedFormGroup): boolean {
    const status = group.get('status')?.value;
    return status === MedicationStatus.ON_HOLD || status === MedicationStatus.STOPPED;
  }

  private buildSmartDefaults(previous: AbstractControl): Partial<Medication> {
    return {
      frequency: previous.get('frequency')?.value,
      hours: this.serializeHours(previous as UntypedFormGroup),
      status: previous.get('status')?.value,
    };
  }

  private buildMedicationGroup(seed?: Partial<Medication>): UntypedFormGroup {
    return this.fb.group(
      {
        id: [seed?.id ?? ''],
        name: [seed?.name ?? '', Validators.required],
        dosage: [seed?.dosage ?? ''],
        dosageAmount: [seed?.dosageAmount ?? '', Validators.required],
        dosageMeasure: [seed?.dosageMeasure ?? '', Validators.required],
        drugForm: [seed?.form ?? ''],
        frequency: [
          seed?.frequency ? (this.isValidFrequency(seed.frequency) ? seed.frequency : 'Other') : '',
          Validators.required,
        ],
        otherFrequency: [seed?.frequency && !this.isValidFrequency(seed.frequency) ? seed.frequency : ''],
        selectedHours: this.fb.group({
          [MedicationHours.AM]: [(seed?.hours ?? []).includes(MedicationHours.AM)],
          [MedicationHours.PM]: [(seed?.hours ?? []).includes(MedicationHours.PM)],
          [MedicationHours.EVENING]: [(seed?.hours ?? []).includes(MedicationHours.EVENING)],
          [MedicationHours.AS_NEEDED]: [(seed?.hours ?? []).includes(MedicationHours.AS_NEEDED)],
        }),
        status: [seed?.status ?? MedicationStatus.ACTIVE],
        statusReason: [seed?.statusReason ?? ''],
        priorPrescription: [seed?.priorPrescription ?? ''],
        reminder: [false],
        reminderFrequency: ['every_day'],
        reminderDays: this.fb.group(this.days.reduce((acc, d) => ({ ...acc, [d.key]: new UntypedFormControl(false) }), {})),
        reminderTimes: this.fb.array([this.createTime('11:00 AM')]),
      },
      { validators: otherFrequencyValidator }
    );
  }

  private initializeForm(): void {
    const initial = this.buildMedicationGroup(this.isEditMode ? this.data : undefined);
    this.medicationsArray = this.fb.array([initial]);
    this.expandedIndex = 0;
    this.attachNameFilter(initial);
    if (this.isEditMode) {
      this.loadEditModeReminder();
    }
  }

  private loadEditModeReminder(): void {
    if (!this.data?.id) {
      return;
    }
    this.fhirService.getMedicationReminder(this.patientService.currentPatientServiceID, this.data.id).then((res) => {
      if (!res) {
        return;
      }
      this.reminderId = res.id;
      const group = this.medicationsArray.at(0) as UntypedFormGroup;
      group.get('reminder')?.setValue(res.isActive);
      group.get('reminderFrequency')?.setValue(res.frequency);
      group.get('reminderDays')?.setValue(res.days);
      const times = this.reminderTimesAt(0);
      times.clear();
      for (const time of res.times ?? []) {
        times.push(this.createTime(time));
      }
    });
  }

  private attachNameFilter(group: UntypedFormGroup): void {
    const obs = group.get('name').valueChanges.pipe(
      startWith(''),
      distinctUntilChanged(),
      filter((value) => value && value.length > 2),
      tap(() => (this.loading = true)),
      debounceTime(200),
      map((value) => this.filterDisplayNames(value)),
      tap(() => (this.loading = false))
    );
    this.filteredOptions.push(obs);
  }

  private filterDisplayNames(name: string): string[] {
    const filterValue = name.toLowerCase();
    return this.options.filter((option) => option.toLowerCase().includes(filterValue));
  }

  private createTime(value: string): UntypedFormControl {
    return this.fb.control(value, Validators.required);
  }

  private listenChangeTheme(): void {
    this.themeService.isLightModeObservable$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (isLightMode) => (this.isLightMode = isLightMode),
      error: (error) => console.error('Error listening to theme changes:', error),
    });
  }

  private getDisplayNames(): void {
    this.fhirService
      .getDisplayTerms()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: NihAutoComplete) => {
          this.options = data.displayTermsList.term;
        },
        error: (error) => {
          console.error('Error loading medication display terms:', error);
          this.snackBarService.genericSnackBar('Error loading medication names', ['error-snackbar']);
        },
      });
  }

  private formatEnumLabel(value: string): string {
    return value
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private formatReminderFrequencyLabel(value: string): string {
    const labels = {
      [MedicationReminderFrequency.EVERY_DAY]: 'Every day',
      [MedicationReminderFrequency.SPECIFIC_DAYS]: 'On specific days of the week',
    };
    return labels[value];
  }

  private getWeekDayLabel(value: string): string {
    const labels = {
      [WeekDay.SUNDAY]: 'S',
      [WeekDay.MONDAY]: 'M',
      [WeekDay.TUESDAY]: 'T',
      [WeekDay.WEDNESDAY]: 'W',
      [WeekDay.THURSDAY]: 'T',
      [WeekDay.FRIDAY]: 'F',
      [WeekDay.SATURDAY]: 'S',
    };
    return labels[value];
  }
}
